import { useEffect, useRef } from 'react';

import { type ToastRecord, getToastAnnouncement } from './Toast.common';
import { getVisibleToasts, isLeaving } from './toastListReducer';

type Announced = { announcement: string; withdraw: () => void };

/**
 * Speaks for a stack's toasts, which drop their own announcement (`announce={false}`): each once,
 * in arrival order, and again if its text changes. One evicted before it could be read is
 * withdrawn.
 *
 * `announce` must be referentially stable, and returns a callback that withdraws its message.
 * `index` is the position in this pass, for a platform that staggers a burst.
 */
const useToastAnnouncements = (
  toasts: readonly ToastRecord[],
  maxVisible: number,
  announce: (message: string, index: number) => () => void
): void => {
  // A ref, so a reflow can never re-announce.
  const announced = useRef(new Map<string, Announced>()).current;

  useEffect(() => {
    const visible = getVisibleToasts(toasts, maxVisible);
    const visibleIds = new Set(visible.map((toast) => toast.id));
    announced.forEach((entry, id) => {
      if (!visibleIds.has(id)) {
        entry.withdraw();
        announced.delete(id);
      }
    });

    visible.forEach((toast, index) => {
      // It has had whatever announcement it was going to get.
      if (isLeaving(toast)) return;

      const announcement = getToastAnnouncement(toast);
      const previous = announced.get(toast.id);
      if (previous?.announcement === announcement) return;

      previous?.withdraw();
      announced.set(toast.id, { announcement, withdraw: announce(announcement, index) });
    });
  }, [toasts, maxVisible, announce, announced]);

  useEffect(
    () => () => {
      announced.forEach((entry) => entry.withdraw());
      announced.clear();
    },
    [announced]
  );
};

export default useToastAnnouncements;
